import { useState } from "react";
import { useTranslation } from "react-i18next";

import type {
  CustomerProductionProgress,
  CustomerTimelineEvent,
} from "@/api/types";
import { formatCalendarDate } from "@/lib/labels";
import { pickupKey } from "@/lib/pickupVocabulary";
import { useProcessTypeLabel } from "@/lib/useProcessTypeLabel";

/**
 * Stav objednávky tak, jak ho vidí zákazník — časová osa a průběh výroby.
 *
 * KAN-1653 — jedna komponenta pro ``/track`` i detail objednávky. Obě
 * stránky dostávají data z téhož BE builderu
 * (``self_service.build_customer_order_status``); do té doby měl detail
 * vlastní výpočet průběhu ve FE a u doručené zakázky ukazoval krok bez ✓.
 */

export function CustomerOrderTimeline({
  timeline,
  locale,
  pickup = false,
}: {
  timeline: CustomerTimelineEvent[];
  locale: string;
  /** KAN-2116 — osobní odběr čte milníky slovníkem odběru („Vydáno"), jinak
   *  osa říkala „Doručeno" vedle „Stav: Vydáno". */
  pickup?: boolean;
}): JSX.Element | null {
  const { t } = useTranslation("track");
  if (timeline.length === 0) return null;
  return (
    <section
      className="mt-4 rounded-lg border border-border bg-surface p-4"
      data-testid="track-lifecycle-timeline"
    >
      <h3 className="text-sm font-semibold uppercase tracking-wide text-ink-3">
        {t("result.timeline")}
      </h3>
      <ol className="mt-3 space-y-2">
        {timeline.map((item) => (
          <li
            key={`${item.key}-${item.occurred_at ?? item.state}`}
            className="flex items-center justify-between gap-3 text-sm"
            data-testid="track-lifecycle-timeline-item"
            data-status={item.status}
          >
            <span className="min-w-0 truncate text-ink">
              {t(pickupKey(`status.${item.status}`, pickup, "track"), {
                defaultValue: item.status,
              })}
            </span>
            {item.occurred_at && (
              <span className="shrink-0 text-xs text-ink-3">
                {formatCalendarDate(item.occurred_at, locale)}
              </span>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

export function CustomerProductionProgressCard({
  progress,
  onDownloadProtocol,
}: {
  progress: CustomerProductionProgress;
  /** Jen ``/track`` — protokol chrání dvojice číslo + e-mail. */
  onDownloadProtocol?: (workItemId: number) => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation("track");
  const { t: tOrder } = useTranslation("order");
  const processLabel = useProcessTypeLabel();
  const [protocolError, setProtocolError] = useState<string | null>(null);
  return (
    <section
      className="mt-4 rounded-lg border border-border bg-surface p-4"
      data-testid="track-progress"
    >
      <h3 className="text-sm font-semibold uppercase tracking-wide text-ink-3">
        {t("result.progress")}
      </h3>
      <ol className="mt-3 flex flex-wrap items-stretch gap-y-3">
        {progress.steps.map((step, idx) => {
          const isLast = idx === progress.steps.length - 1;
          const total = step.total_count ?? 0;
          const completed = step.completed_count ?? 0;
          const active = step.active_count ?? 0;
          return (
            <li
              key={step.key}
              className="flex min-w-0 flex-1 items-center"
              data-testid="track-progress-step"
              data-state={step.state}
            >
              <div className="flex min-w-0 flex-col items-center text-center">
                <span
                  className={[
                    "flex h-7 w-7 items-center justify-center rounded-full border text-xs font-semibold tabular-nums",
                    step.state === "done"
                      ? "border-ok bg-ok text-white"
                      : "",
                    step.state === "current"
                      ? "border-ink bg-ink text-bg"
                      : "",
                    step.state === "upcoming"
                      ? "border-border-strong bg-surface text-ink-3"
                      : "",
                  ].join(" ")}
                >
                  {step.state === "done" ? "✓" : idx + 1}
                </span>
                <span
                  className={
                    step.state === "current"
                      ? "mt-1.5 max-w-[9rem] text-xs font-semibold text-ink"
                      : "mt-1.5 max-w-[9rem] text-xs text-ink-2"
                  }
                  data-testid="track-progress-step-label"
                >
                  {/* KAN-1653 — operace se jmenuje názvem z katalogu
                      obchodu; ``production.processType.*`` pokrývá jen
                      systémové kroky (Přijetí, Odeslání). */}
                  {step.process_type
                    ? processLabel(step.process_type)
                    : tOrder(`production.${step.label_key}`, {
                        defaultValue: step.label_key,
                      })}
                </span>
                {/* KAN-1882 — šarže zbylých kusů; bez popisku zákazník
                    viděl dvakrát „Laserové řezání" a nevěděl proč. */}
                {step.is_remake && (
                  <span
                    className="mt-1 max-w-[9rem] text-[11px] leading-tight text-ink-3"
                    data-testid="track-progress-step-remake"
                  >
                    {tOrder("production.remake")}
                  </span>
                )}
                {total > 1 && (
                  <span className="mt-1 max-w-[9rem] text-[11px] leading-tight text-ink-3">
                    {tOrder("production.workflow.branchSummary", {
                      completed,
                      active,
                      total,
                    })}
                  </span>
                )}
                {step.verdict_outcome && (
                  <span
                    className="mt-1 max-w-[9rem] text-[11px] leading-tight text-ink-2"
                    data-testid="track-gate-verdict"
                  >
                    {tOrder(`production.gate.${step.verdict_outcome}`, {
                      defaultValue: step.verdict_outcome,
                    })}
                  </span>
                )}
                {onDownloadProtocol && step.protocol_work_item_id != null && (
                  <button
                    type="button"
                    className="mt-1 max-w-[9rem] text-[11px] leading-tight text-ink underline"
                    data-testid="track-gate-protocol"
                    onClick={() => {
                      setProtocolError(null);
                      void onDownloadProtocol(
                        step.protocol_work_item_id as number,
                      ).catch(() => setProtocolError(t("error.generic")));
                    }}
                  >
                    {t("result.protocol")}
                  </button>
                )}
              </div>
              {!isLast && (
                <div
                  className={
                    step.state === "done"
                      ? "mx-2 h-px flex-1 bg-ok"
                      : "mx-2 h-px flex-1 bg-border-strong"
                  }
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
      {progress.alternative_path_count > 0 && (
        <p className="mt-3 text-xs italic text-ink-3">
          {tOrder("production.alternativePath", {
            count: progress.alternative_path_count,
          })}
        </p>
      )}
      {protocolError && (
        <p className="mt-3 text-xs text-danger" data-testid="track-gate-protocol-error">
          {protocolError}
        </p>
      )}
    </section>
  );
}
